import { UserWithEmail } from "lens-platform-sdk";

import { recurly } from "src/constants";
import { formatCurrency } from "src/utils/currency";
import { LicensedToken } from "src/components/pages/Home/license";

export const getRecurlyHostedPaymentPageUrl = (
  planType: PlanCode, profile: UserWithEmail, token: LicensedToken
) => `${recurly}/subscribe/${planType}/${encodeURIComponent(token.sub ?? "")}?quantity=1&email=${encodeURIComponent(profile.email)}&username=${encodeURIComponent(profile.username ?? "")}`;

export const getRecurlyHostedPaymentPageUrlForLBID = (
  planType: PlanCode, profile: UserWithEmail, businessId: string, businessName: string, quantity: number = 1
) => `${recurly}/subscribe/${planType}/${encodeURIComponent(businessId)}?quantity=${quantity}&email=${encodeURIComponent(profile.email)}&company_name=${encodeURIComponent(businessName)}`;

export const planCodes: PlanCode[] = ["personal", "pro-monthly", "pro-yearly", "pro-trial"];
const proFeatures = [
  "Lens Desktop",
  "Lens Teamwork",
  "Lens Security Center",
  "Lens Desktop Kubernetes",
  "Community Forums & Slack",
  "24 x 5 Commercial Support"
];

export type PlanCode = "pro-trial" | "pro-yearly" | "pro-monthly" | "personal";

/**
 * Get plan name from a `planCode`.
 */
export const getPlanName = (planCode?: string | null) => {
  switch (planCode) {
    case "pro-trial":
      return "Lens Pro (Trial)";
    case "pro-yearly":
      return "Lens Pro (Annual)";
    case "pro-monthly":
      return "Lens Pro (Monthly)";
    case "personal":
      return "Lens Personal";
    default:
      return "";
  }
};

/**
 * Temporary place for plans' text content, prices and features
 */
const proMonthlyPrice = 19.90;
const proAnnualPrice = 199;

type Price = {
  text: string;
  value: number;
};
export type PlanMetadata = {
  type: "personal" | "pro";
  nameShort: string;
  planSideNote: Array<string>;
  licenseName: string;
  licenseDescription: string;
  features: Array<string>,
  price: {
    monthly: Price;
    annual: Price;
  }
};
export const personalEligibilityText = "You or your company have less than $10M in annual revenue or funding.";
export const planMetadata: {
  personal: PlanMetadata;
  pro: PlanMetadata;
  proTrial: PlanMetadata;
} = {
  personal: {
    type: "personal",
    nameShort: "Personal",
    planSideNote: ["Eligibility:", personalEligibilityText],
    licenseName: "Lens Personal",
    licenseDescription: "Start Lens experience with the free version of Lens Desktop.",
    features: [
      "Lens Desktop",
      "Community Forums & Slack"
    ],
    price: {
      monthly: {
        text: "Free",
        value: 0
      },
      annual: {
        text: "Free",
        value: 0
      }
    }
  },
  pro: {
    type: "pro",
    nameShort: "Pro",
    planSideNote: ["Free 30-day trial available", "No credit card required."],
    licenseName: "Lens Pro",
    licenseDescription: "Complete solution for cloud native professionals.",
    features: proFeatures,
    price: {
      monthly: {
        text: formatCurrency(proMonthlyPrice),
        value: proMonthlyPrice
      },
      annual: {
        text: formatCurrency(proAnnualPrice),
        value: proAnnualPrice
      }
    }
  },
  proTrial: {
    type: "pro",
    nameShort: "Pro Trial",
    planSideNote: ["Free 30-day trial available", "No credit card required."],
    licenseName: "Lens Pro Trial",
    licenseDescription: "Complete solution for cloud native professionals.",
    features: proFeatures,
    price: {
      monthly: {
        text: formatCurrency(proMonthlyPrice),
        value: proMonthlyPrice
      },
      annual: {
        text: formatCurrency(proAnnualPrice),
        value: proAnnualPrice
      }
    }
  }
};

export const getPlanData = (planCode: PlanCode) => {
  switch (planCode) {
    case "pro-monthly":
    case "pro-yearly":
    case "pro-trial":
      return planMetadata.pro;
    default:
      return planMetadata.personal;
  }
};
export type PlanPriceData = {
  title: string;
  perText: string;
  perSeatText: string;
  value: number;
};

export const getPriceData = (planCode: PlanCode): PlanPriceData => {
  const planData = getPlanData(planCode);

  switch (planCode) {
    case "pro-monthly":
      return {
        title: getPlanName(planCode),
        perText: "per month",
        perSeatText: "per seat / month",
        value: planData.price.monthly.value
      };
    case "pro-yearly":
      return {
        title: getPlanName(planCode),
        perText: "per year",
        perSeatText: "per seat / year",
        value: planData.price.annual.value

      };
    default:
      return {
        title: getPlanName(planCode),
        perText: "per month",
        perSeatText: "per seat / month",
        value: 0
      };
  }
};
